import React from 'react'
import ReactDOM from 'react-dom'
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  DefaultOptions,
} from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { setContext } from '@apollo/client/link/context'
import { BrowserRouter as Router } from 'react-router-dom'

import App from './App'
import { getAuthToken } from './Global/Utils'
import { GQL_SERVER_ENDPOINT, LAUNCH_DARKLY, SERVICE_VERSION } from './constants'
import Auth0ContextProvider from './Global/Auth0Context'
import { GlobalProvider } from './Global/GlobalContext'
import '@truepill/react-capsule/dist/default-theme.css'
import { DrawerProvider } from './Global/DrawerContext'
import { createUploadLink } from 'apollo-upload-client'
import { v4 as UUIDv4 } from 'uuid'
import { AsyncProviderConfig, withLDProvider } from 'launchdarkly-react-client-sdk'

const uploadLink = createUploadLink({
  uri: GQL_SERVER_ENDPOINT,
  credentials: 'include',
})

const authLink = setContext((_, { headers }) => {
  const correlationId = UUIDv4()
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${getAuthToken()}`,
      'emr-correlation-id': correlationId,
    },
  }
})

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
}

const client = new ApolloClient({
  //@ts-ignore
  link: authLink.concat(uploadLink),
  cache: new InMemoryCache({
    addTypename: false,
  }),
  defaultOptions,
})

const LDConfig: AsyncProviderConfig = {
  clientSideID: LAUNCH_DARKLY.CLIENT_ID!,
  options: {
    application: {
      id: 'emr-v2-fe',
      version: SERVICE_VERSION,
    }
  }
};

if (process.env.REACT_APP_DD_TOKEN) {
  const ddHigherEnvironmentRegex = /https:\/\/(telehealth\.)?(truepill|stagingpill|falsepill)\.com/g
  const ddLocalDevRegex = /http:\/\/localhost:3002/g

  datadogRum.init({
    applicationId: process.env.REACT_APP_DD_APP_ID || '',
    clientToken: process.env.REACT_APP_DD_TOKEN || '',
    env: process.env.REACT_APP_DD_ENV || 'local',
    service: process.env.REACT_APP_DD_SERVICE || 'emr-v2',
    site: process.env.REACT_APP_DD_SITE || 'us5.datadoghq.com',

    allowedTracingUrls: [
      { match: process.env.NODE_ENV === 'development' ? ddLocalDevRegex : ddHigherEnvironmentRegex, propagatorTypes: ['tracecontext'] },
    ],
    compressIntakeRequests: true,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackLongTasks: true,
    traceSampleRate: 20,
    trackResources: true,
    trackUserInteractions: true
  })
}

const wrappedApp = () => {
  return (
    <ApolloProvider client={client}>
      <Router>
        <Auth0ContextProvider>
          <DrawerProvider>
            <GlobalProvider>
              <App />
            </GlobalProvider>
          </DrawerProvider>
        </Auth0ContextProvider>
      </Router>
    </ApolloProvider>
  )
}

const LDWrappedApp = withLDProvider(LDConfig)(wrappedApp)

const rootElement = document.getElementById('root')
ReactDOM.render(
  <React.StrictMode>
    <LDWrappedApp />
  </React.StrictMode>
  ,
  rootElement
)
